import type {
  ApprovePromotionRequest,
  FormattedPromotions,
  PromotionsQueryParams,
  PromotionsResponse
} from '@library/types/promotions';
import type {
  CreateMissionConditionPropertiesRequest,
  CreateMissionConditionPropertiesResponse,
  CreateMissionRequest,
  CreateMissionResponse,
  FormattedMissions,
  Mission,
  MissionByIdRequest,
  MissionConditionsRequest,
  MissionConditionsResponse,
  MissionEventDefinitionPropertiesRequest,
  MissionEventDefinitionPropertiesResponse,
  MissionEventDefinitionPropertyRequest,
  MissionEventDefinitionPropertyResponse,
  MissionEventDefinitionsResponse,
  MissionResponse,
  MissionsRequest
} from '@prizepicks/component-library/src/types/missions';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { transformPromotions } from '@library/utils/promotions';

const { admin } = apiPaths;

export const promotionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMissions: build.query<FormattedMissions | undefined, MissionsRequest>({
      query: ({ page, perPage }) => ({
        url: `${admin}challenges`,
        method: 'GET',
        params: page && perPage ? { page, per_page: perPage } : {},
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: ['Missions']
    }),
    getMissionById: build.query<Mission, MissionByIdRequest>({
      query: ({ challenge_id }) => ({
        url: `${admin}challenges/${challenge_id}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: MissionResponse) => response.challenge
    }),
    createMission: build.mutation<CreateMissionResponse, CreateMissionRequest>({
      query: ({ mission }) => ({
        url: `${admin}challenges`,
        method: 'POST',
        body: mission,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      invalidatesTags: ['Missions']
    }),
    getMissionEventDefinitions: build.query<
      MissionEventDefinitionsResponse,
      undefined
    >({
      query: () => ({
        url: `${admin}event_definitions`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: ['MissionEventDefinitions']
    }),
    createMissionConditions: build.mutation<
      MissionConditionsResponse,
      MissionConditionsRequest
    >({
      query: (body) => ({
        url: `${admin}challenges/conditions`,
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      invalidatesTags: ['MissionConditions']
    }),
    getMissionEventDefinitionProperties: build.query<
      MissionEventDefinitionPropertiesResponse,
      MissionEventDefinitionPropertiesRequest
    >({
      query: ({ event_definition_id }) => ({
        url: `${admin}event_definitions/${event_definition_id}/properties`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: ['MissionEventDefinitionProperties']
    }),
    getMissionEventDefinitionProperty: build.query<
      MissionEventDefinitionPropertyResponse,
      MissionEventDefinitionPropertyRequest
    >({
      query: ({ event_definition_id, property_id }) => ({
        url: `${admin}event_definitions/${event_definition_id}/properties/${property_id}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      providesTags: ['MissionEventDefinitionProperty']
    }),
    createMissionConditionProperties: build.mutation<
      CreateMissionConditionPropertiesResponse,
      CreateMissionConditionPropertiesRequest
    >({
      query: ({ challenge_id, ...body }) => ({
        url: `${admin}challenges/${challenge_id}/conditions/properties`,
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      invalidatesTags: ['MissionConditions']
    }),
    getPromotions: build.query<
      FormattedPromotions | undefined,
      PromotionsQueryParams
    >({
      query: ({ page, perPage = 25 }) => ({
        url: `${admin}promotions`,
        method: 'GET',
        params: {
          page,
          per_page: perPage
        },
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: PromotionsResponse) =>
        transformPromotions(response),
      providesTags: ['Promotions']
    }),
    getPromotionsPendingApproval: build.query<
      FormattedPromotions | undefined,
      PromotionsQueryParams | undefined
    >({
      query: ({ page = 1, perPage = 25 }: PromotionsQueryParams) => ({
        url: `${admin}promotions/pending_approval`,
        method: 'GET',
        params: {
          page,
          per_page: perPage
        },
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: PromotionsResponse) =>
        transformPromotions(response),
      providesTags: ['Promotions']
    }),
    getApprovedPromotions: build.query<
      FormattedPromotions | undefined,
      PromotionsQueryParams | undefined
    >({
      query: ({ page = 1, perPage = 25 }: PromotionsQueryParams) => ({
        url: `${admin}promotions/approved`,
        method: 'GET',
        params: {
          page,
          per_page: perPage
        },
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: PromotionsResponse) =>
        transformPromotions(response),
      providesTags: ['Promotions']
    }),
    getExpiredPromotions: build.query<
      FormattedPromotions | undefined,
      PromotionsQueryParams | undefined
    >({
      query: ({ page = 1, perPage = 25 }: PromotionsQueryParams) => ({
        url: `${admin}promotions/expired`,
        method: 'GET',
        params: {
          page,
          per_page: perPage
        },
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: PromotionsResponse) =>
        transformPromotions(response),
      providesTags: ['Promotions']
    }),
    approvePromotion: build.mutation<void, ApprovePromotionRequest>({
      query: ({ promotionId }) => ({
        url: `${admin}promotions/${promotionId}/approve`,
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }
      }),
      invalidatesTags: ['Promotions']
    })
  })
});

export const {
  useGetMissionsQuery,
  useGetMissionByIdQuery,
  useCreateMissionMutation,
  useGetMissionEventDefinitionsQuery,
  useCreateMissionConditionsMutation,
  useCreateMissionConditionPropertiesMutation,
  useGetMissionEventDefinitionPropertiesQuery,
  useGetMissionEventDefinitionPropertyQuery,
  useGetPromotionsQuery,
  useGetPromotionsPendingApprovalQuery,
  useGetApprovedPromotionsQuery,
  useGetExpiredPromotionsQuery,
  useApprovePromotionMutation
} = promotionsApi;
